import { RouteGuard } from '@brainfocus/web/router/router';
import { isTrialSignupEnabled } from '@brainfocus/web/services/trial_signup';

let currentUser: CurrentUser | null | undefined;

export const IS_LOGGED_IN: RouteGuard = () => getCurrentUser() ? true : { redirect: '/login' };

export const IS_ANON: RouteGuard = () => !getCurrentUser() ? true : { redirect: '/' };

export const IS_ADMIN: RouteGuard = () => {
    const role = getCurrentUser()?.role;
    return role === 'institution_admin' || role === 'super_admin';
};

export const IS_SUPER_ADMIN: RouteGuard = () => {
    const role = getCurrentUser()?.role;
    return role === 'super_admin' ? true : { redirect: '/admin' };
};

export const IS_NOT_TRIAL: RouteGuard = () => {
    const user = getCurrentUser();
    return user && !user.isTrial ? true : { redirect: '/' };
};

export const IS_TRIAL_SIGNUP_ENABLED: RouteGuard = () =>
    isTrialSignupEnabled() ? true : { redirect: '/' };

/**
 * Get basic information about the current user
 */
export function getCurrentUser(): CurrentUser | null {
    if (currentUser === undefined) {
        const idStr = (document.head.querySelector('meta[name=bf-user-id]') as HTMLMetaElement)?.content;
        const email = (document.head.querySelector('meta[name=bf-user-email]') as HTMLMetaElement)?.content;
        const role = (document.head.querySelector('meta[name=bf-user-role]') as HTMLMetaElement)?.content;
        const creditsStr = (document.head.querySelector('meta[name=bf-user-credits]') as HTMLMetaElement)?.content;
        const institutionIdStr = (document.head.querySelector('meta[name=bf-user-institution-id]') as HTMLMetaElement)?.content;
        const isTrialStr = (document.head.querySelector('meta[name=bf-user-is-trial]') as HTMLMetaElement)?.content;
        const flagsStr = (document.head.querySelector('meta[name=\'bf-feature-flags\']') as HTMLMetaElement)?.content;
        const id = parseInt(idStr);
        const credits = creditsStr === 'None' ? null : parseInt(creditsStr);
        const institutionId = institutionIdStr && institutionIdStr !== 'None' ? parseInt(institutionIdStr) : undefined;
        const isTrial = isTrialStr === 'True';
        const flags = parseFlags(flagsStr);
        if (email && !Number.isNaN(id)) {
            currentUser = { id, email, role, credits, institutionId, isTrial, flags };
        } else {
            currentUser = null;
        }
    }

    return currentUser;
}

function parseFlags(raw: string | undefined): Record<string, boolean> {
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const out: Record<string, boolean> = {};
            for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
                out[k] = v === true;
            }
            return out;
        }
    } catch {
        // ignore malformed JSON
    }
    return {};
}

/**
 * Check role whether current user is an admin (institution_admin or super_admin)
 */
export function isAdmin(): boolean {
    const role = getCurrentUser()?.role;
    return role === 'institution_admin' || role === 'super_admin';
}

/**
 * Check whether current user is a super admin
 */
export function isSuperAdmin(): boolean {
    return getCurrentUser()?.role === 'super_admin';
}

export interface CurrentUser {
    id: number;
    email: string;
    role: string;
    credits: number | null;
    institutionId?: number;
    isTrial: boolean;
    flags?: Record<string, boolean>;
}
